import { useCallback, useMemo } from "react"
import { useTheme } from "@mui/material"
import { get } from "lodash"
import { useNavigate } from "react-router-dom"

import { prepareTextByFieldName } from "utils/string"
import { usePlaceDetailMap } from "hooks/place/usePlaceDetailMap"
import { useGeoLocation } from "providers/geolocation"
import { useFeatureDetail } from "hooks/feature"
import { usePageSideBarLayout } from "providers/ui/hooks"
import { useTranslation } from "providers/i18n"

import { PageCard } from "components/card"

import { ImdfFeatureData } from "providers/venue/types"
import { PlaceDetailSideBar } from "components/domains/features/PlaceDetailSideBar"

export const PlaceDetailKioskHPage = ({ Layout }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const data = useFeatureDetail()
  const { feature, boothName, location: locationName, level } = data

  usePlaceDetailMap(feature as ImdfFeatureData)

  const { deviceLocation } = useGeoLocation()

  const isReadyToNavigate = !!deviceLocation && get(data, "isNavigatable", true)

  const { pageCardSideBar } =
    theme.components[`PlaceDetailPage`]?.styleOverrides || {}

  const { locationTextKeys, locationSeparator, locationEmpty } =
    theme.components[`AppDetailPage`]?.defaultProps || {}

  const onNavigate = useCallback(() => {
    if (!isReadyToNavigate) return
    navigate(`/maps/dir/unknown/${data?.id}`)
  }, [navigate, isReadyToNavigate, data?.id])

  const handleClickPromotionButton = useCallback(() => {
    navigate(`/promotions/occupant/${data?.id}`)
  }, [navigate, data?.id])

  const location = useMemo(() => {
    const locationTextObj = {
      locationNameText: t(locationName),
      boothNameText: boothName ? t("Booth Text", { name: t(boothName) }) : "",
      floorText: level ? t("Level Text", { level: t(level) }) : "",
    }
    return prepareTextByFieldName(locationTextObj, locationTextKeys, {
      empty: locationEmpty,
      separator: locationSeparator,
    })
  }, [
    t,
    locationTextKeys,
    locationSeparator,
    locationEmpty,
    locationName,
    boothName,
    level,
  ])

  usePageSideBarLayout({
    sidebar: {
      open: true,
    },
  })

  return (
    <Layout
      aside={
        <PageCard sx={pageCardSideBar}>
          <PlaceDetailSideBar
            data={{ ...data, location }}
            onNavigate={onNavigate}
            onClickPromotionButton={handleClickPromotionButton}
          />
        </PageCard>
      }
    />
  )
}
